import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { UseFormRegisterReturn } from 'react-hook-form';
import { colors } from '@/assets/styles/colors';
import { Text } from '@/components/ui/text/Text';
import { InfoBubble } from '@/components/ui/tooltips/InfoBubble';
import { getInputColors } from './Input.types';
import type { InputColor, InputColors } from './Input.types';

/**
 * TextArea variant types - determines visual structure
 * - floating: Label floats up when focused/has value
 * - standard: Label above textarea, simple border
 */
export type TextAreaVariant = 'floating' | 'standard';

export interface TextAreaProps {
  // Identity
  id?: string;
  'data-testid'?: string;

  // Value & Control
  value?: string;
  onChange?: (value: string) => void;
  registration?: UseFormRegisterReturn;

  // Display
  label?: string;
  tooltipText?: string;
  placeholder?: string;
  variant?: TextAreaVariant;
  color?: InputColor;
  fullWidth?: boolean;
  /**
   * Etiqueta accesible del campo. Necesaria cuando el diseño no muestra `label` visible y el
   * placeholder es el único texto guía: sin esto el `<textarea>` queda sin nombre accesible.
   */
  ariaLabel?: string;

  // State
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  autoFocus?: boolean;

  // Supporting text (shown below textarea)
  supportingText?: string;

  // Validation
  maxLength?: number;

  // TextArea-specific
  rows?: number;
  showCharacterCount?: boolean;
  /**
   * Texto que sigue al contador. El estándar del proyecto es "0/50 caracteres": el componente
   * resuelve ese sufijo por su cuenta desde `common:validation.characterCountSuffix`, así que
   * NO hace falta pasarlo en cada pantalla.
   *
   * Solo se informa para apartarse del estándar: otra palabra cuando el límite no cuenta
   * caracteres, o `''` para dejar el contador desnudo ("0/50"). `undefined` = estándar.
   */
  characterCountSuffix?: string;

  // Events
  onFocus?: () => void;
  onBlur?: () => void;

  // Styling
  className?: string;
  inputClassName?: string;
  labelClassName?: string;
  /**
   * Estilos inline para el `<textarea>`. Se mergean DESPUÉS de los que arma el componente
   * (`borderColor` / `color` / `backgroundColor`), así que pueden sobrescribirlos.
   *
   * Es la vía correcta para tocar el radio de las esquinas: `inputClassName="rounded-tl-lg"`
   * compite con el `rounded-tl-none` de la variante con la misma especificidad, y el ganador
   * depende del orden en que Tailwind emita las clases.
   */
  inputStyle?: React.CSSProperties;

  // Escape hatch for custom colors (like LabelInput's customColors)
  customColors?: Partial<InputColors>;
}

export const TextArea: React.FC<TextAreaProps> = ({
  id,
  'data-testid': dataTestId,

  value = '',
  onChange,
  registration,

  label,
  tooltipText,
  placeholder,
  variant = 'standard',
  color = 'default',
  fullWidth = true,
  ariaLabel,

  disabled = false,
  readOnly = false,
  required = false,
  autoFocus = false,

  supportingText,

  maxLength,

  rows = 4,
  showCharacterCount = false,
  characterCountSuffix,

  onFocus,
  onBlur,

  className = '',
  inputClassName = '',
  labelClassName = '',
  inputStyle,

  customColors,
}) => {
  const { t: translate } = useTranslation();

  const [isFocused, setIsFocused] = useState(false);
  const [internalValue, setInternalValue] = useState('');

  // Sufijo del contador: estándar del proyecto salvo que la pantalla lo sobrescriba. Se resuelve
  // con `??` y no con un default de destructuring porque el valor sale de un hook; de paso, eso
  // deja `''` como la forma explícita de pedir el contador sin sufijo.
  const resolvedCharacterCountSuffix =
    characterCountSuffix ?? translate('common:validation.characterCountSuffix');

  // Sincronizar el estado interno cuando cambie el valor externo (solo en modo controlado).
  // En modo `registration` el propio onChange ya actualiza internalValue en cada tecla —
  // sincronizar aquí contra `value` (que nunca cambia, pues no se pasa en ese modo) lo
  // resetearía a '' en cada render.
  useEffect(() => {
    if (registration) return;
    if (value !== undefined && value !== internalValue) {
      setInternalValue(value);
    }
  }, [value, internalValue, registration]);

  const textAreaValue = registration ? internalValue : value;
  const hasValue = textAreaValue?.length > 0;
  const isActive = isFocused || hasValue || !!placeholder;

  const textAreaId = id || `textarea-${Math.random().toString(36).substr(2, 9)}`;

  // Get colors from pure function, then apply any custom overrides
  const baseColors = getInputColors({
    color,
    disabled,
    focused: isFocused,
    hasValue,
  });

  const inputColors: InputColors = {
    ...baseColors,
    ...customColors,
  };

  // Variant-specific styles
  const variantStyles = {
    floating: {
      container: 'relative pt-3',
      textArea: 'bg-transparent border border-solid rounded-lg rounded-tl-none focus:ring-0 outline-none',
      label: 'absolute left-3 pointer-events-none transition-all duration-200 will-change-transform bg-white px-1',
      labelPosition: (active: boolean) => active ? '-top-0 text-xs' : 'top-5 text-base',
    },
    standard: {
      container: 'relative',
      textArea: 'bg-transparent border border-solid rounded-lg rounded-tl-none focus:ring-0 outline-none',
      label: 'block mb-1',
      labelPosition: () => '',
    },
  } as const;

  const currentVariant = variantStyles[variant];
  
  const handleFocus = () => {
    setIsFocused(true);
    onFocus?.();
  };

  const handleBlur = () => {
    setIsFocused(false);
    onBlur?.();
  };

  const currentLength = textAreaValue?.length || 0;
  const isOverLimit = maxLength ? currentLength > maxLength : false;

  return (
    <div className={`${currentVariant.container} ${fullWidth ? 'w-full' : ''} ${className}`}>
      {/* Standard variant: label above textarea */}
      {variant === 'standard' && label && (
        <div className="flex items-center justify-between">
          <Text variant="small-regular" color={inputColors.label} className={labelClassName}>
            {label}
            {required && <span className="ml-1">*</span>}
            {tooltipText && <InfoBubble text={tooltipText} className="-top-0.5" />}
          </Text>
        </div>
      )}

      <div className="relative">
        <textarea
          id={textAreaId}
          {...(registration || {})}
          value={registration ? undefined : value}
          placeholder={variant === 'standard' ? placeholder : ''}
          data-testid={dataTestId}
          aria-label={ariaLabel}
          onChange={registration
            ? (event) => {
              void registration.onChange(event);
              setInternalValue(event.target.value);
            }
            : (event) => onChange?.(event.target.value)
          }
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          readOnly={readOnly}
          autoFocus={autoFocus}
          rows={rows}
          maxLength={maxLength ?? undefined}
          className={`
            ${currentVariant.textArea}
            px-3 py-2.5 text-sm
            ${fullWidth ? 'w-full' : ''}
            ${disabled ? 'cursor-not-allowed opacity-50' : ''}
            ${readOnly ? 'cursor-default' : ''}
            resize-none
            font-sans
            transition-all duration-200
            ${inputClassName}
          `}
          style={{
            borderColor: inputColors.border,
            color: inputColors.text,
            backgroundColor: inputColors.background,
            ...inputStyle,
          }}
        />

        {/* Floating variant: animated label */}
        {variant === 'floating' && label && (
          <label
            htmlFor={textAreaId}
            className={`
              ${currentVariant.label}
              ${currentVariant.labelPosition(isActive)}
              ${labelClassName}
            `}
            style={{ color: inputColors.label }}
          >
            {label}
            {required && <span className="ml-1">*</span>}
            {tooltipText && <InfoBubble text={tooltipText} className="-top-1" />}
          </label>
        )}
      </div>

      {/* Supporting text and character count */}
      <div className="flex justify-between items-center">
        <div className="flex">
          {supportingText && (
            <Text
              variant="small-regular"
              color={inputColors.supporting}
              data-testid={color === 'error' ? 'error-message' : undefined}
            >
              {supportingText}
            </Text>
          )}
        </div>

        {showCharacterCount && maxLength && (
          <Text
            variant="small-regular"
            color={isOverLimit ? colors.feedback.error300 : inputColors.supporting}
            data-testid={dataTestId ? `${dataTestId}-char-count` : undefined}
            className="ml-2"
          >
            {currentLength}/{maxLength}{resolvedCharacterCountSuffix ? ` ${resolvedCharacterCountSuffix}` : ''}
          </Text>
        )}
      </div>
    </div>
  );
};

export default TextArea;
